import {
  ApprovalRequestStatus as EscalationStatus,
  ApprovalRequestStatusLevel as EscalationStatusLevel,
  ApprovalRequest,
  EscalationExternalTicket,
  EscalationLevel,
  EscalationRequest,
  EscalationRequestAssignment,
  EscalationApproval,
} from '../../../types/approval-request';
import { parseDateToMs } from '../../../support/utils';

type EscalationStageSummary = {
  level: number;
  approval?: {
    status?: {
      status?: EscalationStatusLevel;
    };
  } | null;
};

export type NextEscalationTarget = {
  targetLevel: number;
  targetAssignmentTypeId: number;
};

type EscalationActionEligibilityParams = {
  userId: number;
  approval?: ApprovalRequest | null;
  assignment?: EscalationRequestAssignment | null;
  escalation: EscalationRequest;
};


export const getEscalationStatusLabel = (
  escalationStage: EscalationLevel,
  escalationStatus: EscalationStatus,
  currentStages: EscalationStageSummary[]
) => {
  switch (escalationStage) {
    case EscalationLevel.Incubation:
      if (
        escalationStatus?.status === EscalationStatusLevel.ReEscalated ||
        escalationStatus?.status === EscalationStatusLevel.Superseded
      ) {
        return 'Assigned to Incubation Team';
      }

      if (
        escalationStatus?.status === EscalationStatusLevel.Approved &&
        currentStages.some((stage) =>
          stage.level === EscalationLevel.Incubation &&
          stage.approval?.status?.status !== EscalationStatusLevel.Denied
        )
      ) {
        return 'Pending Re-escalation';
      }

      return escalationStatus?.title ?? 'Unknown Status';

    case EscalationLevel.Business:
      if (
        escalationStatus?.status === EscalationStatusLevel.ReEscalated ||
        escalationStatus?.status === EscalationStatusLevel.Superseded
      ) {
        return 'Assigned to Business Unit';
      }

      return escalationStatus?.title ?? 'Unknown Status';

    case EscalationLevel.Domain:
      return escalationStatus?.status === EscalationStatusLevel.Approved
        ? 'Assigned to Domain Specialist'
        : escalationStatus?.title ?? 'Unknown Status';

    default:
      return escalationStatus?.title ?? 'Unknown Status';
  }
};

/**
 * Where a BU escalation ticket is viewable when the stored ticket has no `jira_issue_url` of its
 * own - either an older row, or a backend that predates the URL being written. Override with
 * REACT_APP_JIRA_SERVICE_DESK_BASE_URL to point at a different Jira (must match the backend's
 * FIRESTARTER_JIRA_SERVICE_DESK_BASE_URL).
 *
 * This is the JSM customer portal, not the Jira issue view: requesters are service desk
 * customers, not licensed Jira users, so `/browse/<KEY>` is denied to them. The Jira URL is only
 * used for REST calls, which happen server-side.
 */
export const JIRA_SERVICE_DESK_BASE_URL = (
  process.env.REACT_APP_JIRA_SERVICE_DESK_BASE_URL || 'https://netsec-jira.engit-alm.com/jira'
).replace(/\/+$/, '');

/** Matches the backend's BU_SERVICE_DESK_ID, used when a ticket row predates the column. */
export const DEFAULT_BU_SERVICE_DESK_ID = 1;

export const buildJiraServiceDeskUrl = (
  issueKey?: string | null,
  serviceDeskId?: number | string | null
): string | null => {
  const key = (issueKey ?? '').trim();
  if (!key) return null;

  const deskId = serviceDeskId ?? DEFAULT_BU_SERVICE_DESK_ID;

  return `${JIRA_SERVICE_DESK_BASE_URL}/servicedesk/customer/portal/${deskId}/${key}`;
};

/** Shown while the Jira ticket exists locally but nothing has been read back from Jira yet. */
export const JIRA_PENDING_STATUS_LABEL = 'Queued';

const JIRA_FAILED_STATUS_LABEL = 'Jira Request Failed';

/**
 * The escalation's live Jira ticket — the most recently created one, since re-submitting a BU
 * escalation adds a row rather than replacing it.
 */
export const getJiraExternalTicket = (
  escalation?: EscalationRequest | null
): EscalationExternalTicket | null => {
  const tickets = (escalation?.external_tickets ?? []).filter(
    (ticket) => (ticket?.system ?? 'jira') === 'jira'
  );

  if (!tickets.length) return null;

  return tickets.reduce((latest, ticket) => {
    if (!latest) return ticket;

    // Prefer a ticket that actually reached Jira over one that only exists locally.
    const ticketHasIssue = !!(ticket?.jira_issue_key || ticket?.jira_issue_id);
    const latestHasIssue = !!(latest?.jira_issue_key || latest?.jira_issue_id);
    if (ticketHasIssue !== latestHasIssue) return ticketHasIssue ? ticket : latest;

    return parseDateToMs(ticket?.created) > parseDateToMs(latest?.created) ? ticket : latest;
  }, null as EscalationExternalTicket | null);
};

export const getJiraIssueKey = (
  ticket?: EscalationExternalTicket | null,
  escalation?: EscalationRequest | null
): string | null =>
  ticket?.jira_issue_key || ticket?.jira_issue_id || escalation?.jira_issue_id || null;

export const getJiraIssueUrl = (
  ticket?: EscalationExternalTicket | null,
  escalation?: EscalationRequest | null
): string | null => {
  if (ticket?.jira_issue_url) return ticket.jira_issue_url;

  // Only an issue key resolves in the portal; a numeric internal id does not.
  const issueKey = ticket?.jira_issue_key || escalation?.jira_issue_id || null;
  if (!issueKey || /^\d+$/.test(issueKey)) return null;

  return buildJiraServiceDeskUrl(issueKey, ticket?.service_desk_id);
};

/**
 * The request type the escalation was filed as, e.g. "Automation Assistance". `request_type_name`
 * is written when the ticket row is created, so it is there before Jira has ever been read back;
 * the `jira_*` variants are what a sync fills in.
 */
export const getJiraRequestTypeLabel = (
  ticket?: EscalationExternalTicket | null
): string | null =>
  ticket?.request_type_name ||
  ticket?.jira_request_type_label ||
  ticket?.jira_customer_request_type ||
  null;

/**
 * The Jira workflow status, e.g. "Waiting for Support" / "In Progress" / "Done". Falls back to
 * the JSM customer-portal status, then to the pending label when Jira hasn't been read yet.
 */
export const getJiraStatusLabel = (ticket?: EscalationExternalTicket | null): string => {
  if (!ticket) return JIRA_PENDING_STATUS_LABEL;
  if (ticket.status === 'failed' && !ticket.jira_status_name) return JIRA_FAILED_STATUS_LABEL;

  return (
    ticket.jira_status_name ||
    ticket.jira_request_current_status ||
    JIRA_PENDING_STATUS_LABEL
  );
};

export const getJiraAssigneeLabel = (ticket?: EscalationExternalTicket | null): string | null => {
  const assignee =
    ticket?.jira_assignee_display_name ||
    ticket?.jira_assignee_email ||
    ticket?.jira_assignee_username ||
    null;

  if (!assignee) return null;

  return assignee.includes('@') ? assignee.split('@')[0] : assignee;
};

export type JiraTicketHistoryEntry = {
  id: string;
  author: string | null;
  body: string;
  created: string | null;
};

/**
 * The ticket's worklog, newest first, so anyone looking at the escalation can read what has
 * happened on the Jira side without opening the portal.
 *
 * Only the escalation detail lookup hydrates `jira_comments`; the list response leaves it out, so
 * an empty result here means "not loaded" just as often as it means "no comments yet".
 */
export const getJiraTicketHistory = (
  ticket?: EscalationExternalTicket | null
): JiraTicketHistoryEntry[] => {
  const comments = ticket?.jira_comments ?? [];
  if (!Array.isArray(comments)) return [];

  return comments
    .map((comment, index) => {
      const body = typeof comment?.body === 'string' ? comment.body.trim() : '';

      return {
        id: String(comment?.id ?? index),
        author:
          comment?.author?.displayName ||
          comment?.author?.display_name ||
          comment?.author?.emailAddress ||
          comment?.author?.email ||
          comment?.author?.name ||
          null,
        body,
        created: comment?.created ?? comment?.updated ?? null,
      };
    })
    .filter((entry) => !!entry.body)
    .sort((a, b) => parseDateToMs(b.created) - parseDateToMs(a.created));
};

/** A Jira ticket that has reached a resolution needs no further polling. */
export const isJiraTicketResolved = (ticket?: EscalationExternalTicket | null): boolean =>
  !!ticket &&
  (!!ticket.jira_resolution_at ||
    !!ticket.jira_resolution_name ||
    ticket.jira_status_category_key === 'done');

export const getAssignmentDisplayText = ({
  level,
  assignment,
  approval,
  escalation,
  jiraTicket,
}: {
  level: EscalationLevel | number;
  assignment?: EscalationRequestAssignment | null;
  approval?: ApprovalRequest | null;
  escalation?: EscalationRequest | null;
  /**
   * Ticket to read Jira state from, when the caller already has a fresher one than the
   * escalation payload carries (the escalation list does not always hydrate tickets).
   */
  jiraTicket?: EscalationExternalTicket | null;
}) => {
  const assignedEmail = assignment?.assigned_to?.email ?? null;

  if (assignedEmail) {
    const assignedShortName = assignedEmail.split('@')[0];
    return `Assigned: ${assignedShortName ?? assignedEmail}`;
  }

  if (Number(level) === EscalationLevel.Business && assignment) {
    // A BU escalation is worked in Jira, so Jira owns its status. There is no toolbox
    // assignment to read: the label has to come from the synced ticket. It reads
    // "<request type>: <Jira status>", e.g. "Automation Assistance: In Progress" - the
    // assignee is in the expanded detail rather than here, since the request type is what
    // distinguishes one BU escalation from another at a glance.
    const ticket = jiraTicket ?? getJiraExternalTicket(escalation);
    const jiraStatus = getJiraStatusLabel(ticket);
    const requestType = getJiraRequestTypeLabel(ticket);

    return requestType ? `${requestType}: ${jiraStatus}` : jiraStatus;
  }

  if (Number(level) === EscalationLevel.Incubation && assignment) {
    return 'Awaiting SE Assignment';
  }

  if (assignment?.status?.title) return assignment.status.title;
  if (approval?.status?.title) return approval.status.title;
  return '';
};

export const getNextAvailableEscalationLevel = (
  escalation: EscalationRequest,
): NextEscalationTarget | null => {
  const approvals = escalation?.approvals ?? [];
  const highestApproval = approvals.reduce((highest, approval) => {
    const status = approval?.approval_request?.status?.status;
    if ([EscalationStatusLevel.Cancelled, EscalationStatusLevel.Denied].includes(status)) return highest;

    const level = approval?.target_assignment_type?.level;
    if (!highest || (level && level > highest.target_assignment_type?.level)) {
      return approval;
    }
    return highest;
  }, null as EscalationApproval | null);

  const highestApprovalStatus = highestApproval?.approval_request?.status?.status;
  if (
    highestApprovalStatus === EscalationStatusLevel.Pending ||
    highestApprovalStatus === EscalationStatusLevel.UpdateRequired
  ) return null;

  const assignments = escalation?.assignments ?? [];
  const highestAssignment = assignments.reduce((highest, assignment) => {
    const level = assignment?.assignment_type?.level;
    if (!highest || (level && level > highest.assignment_type?.level)) {
      return assignment;
    }
    return highest;
  }, null as EscalationRequestAssignment | null);

  const supportPaths = (escalation?.available_support_paths ?? [])
    .filter((path) => {
      const level = path?.assignment_type_level ?? null;
      return level !== null &&
        level > Math.max(
          highestApproval?.target_assignment_type?.level ?? -1,
          highestAssignment?.assignment_type?.level ?? -1
        );
    })
    .map((path) => ({
      targetLevel: Number(path.assignment_type_level),
      assignmentTypeId: Number(path.assignment_type_id),
    }))
    .sort((a, b) => a.targetLevel - b.targetLevel);

  if (supportPaths.length > 0) {
    const nextPath = supportPaths[0];
    return {
      targetLevel: nextPath.targetLevel,
      targetAssignmentTypeId: nextPath.assignmentTypeId,
    };
  }

  // No higher level available
  return null;
};

export const getRequestorEscalationLevelActions = ({
  userId,
  approval,
  assignment,
  escalation,
}: EscalationActionEligibilityParams) => {
  const isRequestor = !!approval && approval?.requestor?.id === userId;
  const isAssignedToEngineer = !!assignment?.assigned_to?.id ?? null;
  const isAssignedToCurrentUser = !!assignment?.assigned_to?.id && assignment.assigned_to.id === userId;

  const approvalStatus = approval?.status?.status;

  const targetEscalation = getNextAvailableEscalationLevel(escalation);

  const canUpdate =
    isRequestor &&
    !isAssignedToEngineer &&
    approvalStatus === EscalationStatusLevel.UpdateRequired;

  const canCancel =
    isRequestor &&
    !isAssignedToEngineer &&
    [EscalationStatusLevel.Pending, EscalationStatusLevel.UpdateRequired].includes(
      approvalStatus
    );

  // if approval is missing, the assignment occured through superceding a lower level approval request.
  const canEscalate =
    isAssignedToCurrentUser &&
    (!approval || approvalStatus === EscalationStatusLevel.Approved) &&
    !!targetEscalation;

  return {
    canUpdate,
    canCancel,
    canEscalate,
  };
};